import React, { useEffect, useMemo, useRef, useState } from "react";
import "./DateRangePresets.css";

function pad2(n) {
  return String(n).padStart(2, "0");
}
function toYMD(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (!Number.isFinite(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function fromYMD(ymd) {
  if (!ymd) return null;
  const d = new Date(`${ymd}T00:00:00`);
  return Number.isFinite(d.getTime()) ? d : null;
}
function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}
function startOfMonth(date) {
  const d = new Date(date);
  d.setDate(1);
  return d;
}
function endOfMonth(date) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + 1, 0);
  return d;
}

function toBR(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  if (!y || !m || !d) return ymd;
  return `${d}/${m}/${y}`;
}

function subMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() - months);
  return d;
}

export default function DateRangePresets({
  startDate,
  endDate,
  onChange,
  onApply, // (opcional) para carregar relatório ao aplicar
  label = "Período",
}) {
  const wrapRef = useRef(null);
  const popRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [activePreset, setActivePreset] = useState("custom");


  // “draft” dentro do popover (não mexe no estado do pai até aplicar)
  const [draftStart, setDraftStart] = useState(() => toYMD(startDate));
  const [draftEnd, setDraftEnd] = useState(() => toYMD(endDate));

  // Sempre que props mudarem por fora, sincroniza o draft (quando popover fechado)
  useEffect(() => {
    if (open) return;
    setDraftStart(toYMD(startDate));
    setDraftEnd(toYMD(endDate));
  }, [startDate, endDate, open]);

  const prettyLabel = useMemo(() => {
  const s = toYMD(startDate);
  const e = toYMD(endDate);
  if (!s || !e) return "Selecione um período";
  return `${toBR(s)} — ${toBR(e)}`;
}, [startDate, endDate]);


  const presets = [
    { key: "today", label: "Hoje", get: () => [new Date(), new Date()] },
    {
      key: "yesterday",
      label: "Ontem",
      get: () => {
        const d = addDays(new Date(), -1);
        return [d, d];
      },
    },
    {
      key: "7d",
      label: "Últimos 7 dias",
      get: () => {
        const end = new Date();
        const start = addDays(end, -6);
        return [start, end];
      },
    },
    {
      key: "30d",
      label: "Últimos 30 dias",
      get: () => {
        const end = new Date();
        const start = addDays(end, -29);
        return [start, end];
      },
    },
    {
      key: "month",
      label: "Mês atual",
      get: () => {
        const now = new Date();
        return [startOfMonth(now), endOfMonth(now)];
      },
    },
    {
      key: "prevmonth",
      label: "Mês passado",
      get: () => {
        const d = subMonths(new Date(), 1);
        return [startOfMonth(d), endOfMonth(d)];
      },
    },
  ];

  function close() {
    setOpen(false);
  }

function applyDraft(autoClose = true) {
  const s = fromYMD(draftStart);
  const e = fromYMD(draftEnd);
  if (!s || !e) return;

  const startFixed = s <= e ? s : e;
  const endFixed   = s <= e ? e : s;

  onChange?.({ start: startFixed, end: endFixed });

  // ✅ passe as datas para o pai
  onApply?.({ start: startFixed, end: endFixed });

  if (autoClose) close();
}


  // Fecha ao clicar fora
  useEffect(() => {
    function onDocDown(ev) {
      if (!open) return;
      const t = ev.target;
      if (wrapRef.current && wrapRef.current.contains(t)) return;
      close();
    }
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [open]);

  // Fecha com ESC
  useEffect(() => {
    function onKey(ev) {
      if (!open) return;
      if (ev.key === "Escape") close();
      if (ev.key === "Enter") applyDraft(true);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, draftStart, draftEnd]);

  return (
    <div className="drg-wrap" ref={wrapRef}>
      <div className="drg-label">{label}</div>

      <button
  type="button"
  className="drg-trigger"
  onClick={() => {
    setActivePreset("custom");
    setOpen((v) => !v);
  }}
>

        <span className="drg-trigger-text">{prettyLabel}</span>
        <span className="drg-trigger-ico" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="drg-pop" role="dialog" aria-label="Selecionar período">
  <div className="drg-pop-head">
    <div className="drg-pop-title">Selecionar período</div>
    <button type="button" className="drg-x" onClick={close} aria-label="Fechar">×</button>
  </div>

  <div className="drg-body">
    <div className="drg-left">
      <div className="drg-left-title">Personalizar</div>

      <div className="drg-list">
        {presets.map((p) => (
          <button
            key={p.key}
            type="button"
            className={"drg-item" + (p.key === activePreset ? " is-active" : "")}
            onClick={() => {
              const [s, e] = p.get();
              setDraftStart(toYMD(s));
              setDraftEnd(toYMD(e));
              setActivePreset(p.key);
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>

    <div className="drg-right">
      <div className="drg-range-head">
        <div className="drg-field">
          <div className="drg-field-lbl">Data de início</div>
          <input
            className="drg-input"
            type="date"
            value={draftStart}
            onChange={(e) => {
              setDraftStart(e.target.value);
              setActivePreset("custom");
            }}
          />
        </div>

        <div className="drg-field">
          <div className="drg-field-lbl">Data de término</div>
          <input
            className="drg-input"
            type="date"
            value={draftEnd}
            onChange={(e) => {
              setDraftEnd(e.target.value);
              setActivePreset("custom");
            }}
          />
        </div>
      </div>


      <div className="drg-actions">
        <button
          type="button"
          className="drg-btn"
          onClick={() => {
            setDraftStart(toYMD(startDate));
            setDraftEnd(toYMD(endDate));
            setActivePreset("custom");
            close();
          }}
        >
          Cancelar
        </button>

        <button
          type="button"
          className="drg-btn primary"
          onClick={() => applyDraft(true)}
        >
          Aplicar
        </button>
      </div>
    </div>
  </div>
</div>

      )}
    </div>
  );
}
